import React, { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AthleteAttendanceTeam } from '../../../../models/attendance.models';
import { StatsResponse } from '../../../../models/stats.models';
import { MetricMode } from '../../training-log.helpers';
import {
  buildRawDataColumnBase,
  buildRawDataRows,
  hydrateRawDataColumns,
  RawDataColumn,
  RawDataRow,
} from './raw-data.helpers';
import { useRawTableCrosshairHover } from './use-raw-table-crosshair-hover';
import './raw-data.page.css';

interface RawDataTableProps {
  stats: StatsResponse | null;
  attendanceTeams: AthleteAttendanceTeam[] | null | undefined;
  mode: MetricMode;
  onModeChange: (mode: MetricMode) => void;
  onNavigateWeek?: (period: number, week: number) => void;
}

const getCellClassName = (column: RawDataColumn, row: RawDataRow): string => {
  const classes = [column.separator ? 'rt-sep' : '', column.cellClassName ?? ''].filter(Boolean);

  if (column.id === 'week') {
    classes.push('rt-sticky-col', 'rt-sticky-week');
  }

  if (column.id === 'date') {
    classes.push('rt-sticky-col', 'rt-sticky-date');
  }

  const cell = row.cells[column.id];
  if (cell?.className) {
    classes.push(cell.className);
  }

  return classes.join(' ');
};

const RawDataTable: FC<RawDataTableProps> = ({ stats, attendanceTeams, mode, onModeChange, onNavigateWeek }) => {
  const navigate = useNavigate();

  const columnBase = useMemo(() => buildRawDataColumnBase(stats, attendanceTeams), [stats, attendanceTeams]);

  const columns = useMemo(() => hydrateRawDataColumns(columnBase, mode), [columnBase, mode]);

  const rows = useMemo(
    () => buildRawDataRows(stats, attendanceTeams, columns, mode),
    [stats, attendanceTeams, columns, mode]
  );

  const hoverResetKey = `${stats?.totalDuration ?? ''}-${columns.length}-${rows.length}-${mode}`;
  const { tableRef, clearHover, handleMouseMove } = useRawTableCrosshairHover(hoverResetKey);

  const dataColumns = columns.slice(2);

  const handleNavigateWeek = (row: RawDataRow) => {
    if (!row.navigable || row.periodIndex === undefined || row.weekIndex === undefined) {
      return;
    }
    if (onNavigateWeek) {
      onNavigateWeek(row.periodIndex, row.weekIndex);
    } else {
      navigate(`/analytics/training-log?period=${row.periodIndex}&week=${row.weekIndex}`);
    }
  };

  if (!stats || rows.length === 0) {
    return <div className="raw-data-empty">No training data for this season.</div>;
  }

  return (
    <div className="raw-table-wrap">
      <table ref={tableRef} className="raw-table" onMouseMove={handleMouseMove} onMouseLeave={clearHover}>
        <thead>
          <tr>
            <th colSpan={2} className="rt-sticky-col rt-sticky-lead" data-col-index={0}>
              <div className="section-toggle" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className={mode === 'duration' ? 'active' : ''}
                  onClick={() => onModeChange('duration')}
                >
                  Duration
                </button>
                <button
                  type="button"
                  className={mode === 'distance' ? 'active' : ''}
                  onClick={() => onModeChange('distance')}
                >
                  Distance
                </button>
              </div>
            </th>
            {dataColumns.map((col) => (
              <th
                key={col.id}
                className={col.separator ? 'rt-sep' : ''}
                style={col.color ? { color: col.color } : undefined}
                data-col-index={col.colIndex}
              >
                <div className="rt-grp">{col.groupLabel || '\u00A0'}</div>
                <div className="rt-col">{col.subLabel || '\u00A0'}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={`${row.type}-${row.periodIndex ?? 's'}-${row.weekIndex ?? row.weekLabel}`}
              className={row.classNames.join(' ')}
            >
              {columns.map((col) => {
                const cell = row.cells[col.id] ?? { text: '' };

                if (col.id === 'week' && row.navigable) {
                  const isCurrentWeek = row.classNames.includes('rt-current');
                  return (
                    <td
                      key={col.id}
                      className={getCellClassName(col, row)}
                      data-col-index={col.colIndex}
                      data-row-index={rowIndex}
                    >
                      <button
                        type="button"
                        className="rt-wk-link"
                        title="View in Training Log"
                        onClick={() => handleNavigateWeek(row)}
                      >
                        {cell.text}
                      </button>
                      {isCurrentWeek && <span className="rt-current-badge">Current</span>}
                    </td>
                  );
                }

                return (
                  <td
                    key={col.id}
                    className={getCellClassName(col, row)}
                    style={{
                      color: cell.color,
                      fontWeight: cell.fontWeight,
                    }}
                    data-col-index={col.colIndex}
                    data-row-index={rowIndex}
                  >
                    {cell.text}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RawDataTable;
